import { Navigate } from 'react-router-dom';
import { lazy } from 'react';

const MarketingPage = lazy(() => import('../pages/MarketingPage'));
const FeaturesPage = lazy(() => import('../pages/FeaturesPage'));
const GuidePage = lazy(() => import('../pages/GuidePage'));
const FaqPage = lazy(() => import('../pages/FaqPage'));
const BlogPage = lazy(() => import('../pages/BlogPage'));
const PrivacyPage = lazy(() => import('../pages/PrivacyPage'));
const TermsPage = lazy(() => import('../pages/TermsPage'));
const WelcomePage = lazy(() => import('../pages/WelcomePage'));
const DashboardPage = lazy(() => import('../pages/DashboardPage'));
const BookshelfPage = import.meta.env.DEV ? lazy(() => import('../pages/BookshelfPage')) : null;
const InstallPage = lazy(() => import('../pages/InstallPage'));
const ProvisioningPage = lazy(() => import('../pages/ProvisioningPage'));
const AdminPage = lazy(() => import('../pages/AdminPage.jsx'));

// Proto's — experimental/rarely-used pages, excluded from prod builds (same pattern as DevTab,
// see tabDefinitions.js). Vite tree-shakes these branches out entirely when import.meta.env.DEV is false.
const CurrentStatePage = import.meta.env.DEV ? lazy(() => import('../pages/CurrentStatePage')) : null;
const DesignerPage = import.meta.env.DEV ? lazy(() => import('../pages/Designer/index')) : null;
const KlippyPage = import.meta.env.DEV ? lazy(() => import('../features/klippy/KlippyPage.jsx')) : null;

import AppLayout from './AppLayout';
import WorkspaceLayout from './WorkspaceLayout'; // ADDED
import CheckoutCancelledPage from '../pages/CheckoutCancelledPage';
import BeforeYouSubscribePage from '../pages/BeforeYouSubscribePage';

// Public routes — rendered inside AppLayout
const publicChildren = [
    { index: true, element: <MarketingPage /> },
    { path: '/guide', element: <GuidePage /> },
    { path: '/faq', element: <FaqPage /> },
    { path: '/features', element: <FeaturesPage /> },
    { path: '/blog', element: <BlogPage /> },
    { path: '/privacy', element: <PrivacyPage /> },
    { path: '/terms', element: <TermsPage /> },
    { path: '/install', element: <InstallPage /> },
    { path: '/checkoutCancelled', element: <CheckoutCancelledPage /> },
    { path: '/before', element: <BeforeYouSubscribePage /> },
    // Klippy is a proto — dev-only, see routes/index.jsx top comment
    ...(import.meta.env.DEV ? [{ path: '/klippy', element: <KlippyPage /> }] : []),
];

// Authenticated routes inside AppLayout (AppAppBar via AppLayout)
const authenticatedChildren = [
    { path: '/welcome', element: <WelcomePage /> },
    { path: '/provisioning', element: <ProvisioningPage /> },
];

// Workspace routes — rendered inside WorkspaceLayout (fullscreen, no AppAppBar)
// Proto's — dev-only, empty array in prod so WorkspaceLayout gets no children
const workspaceChildren = import.meta.env.DEV
    ? [
          { path: '/currentstate', element: <CurrentStatePage /> },
          { path: '/designer', element: <DesignerPage /> },
      ]
    : [];

export const getRoutes = (isSignedIn) => [
    {
        path: '/',
        element: <AppLayout />,
        children: [...publicChildren, ...(isSignedIn ? authenticatedChildren : [])],
    },
    // Dashboard is standalone — has its own AppAppBar, no AppLayout wrapper
    ...(isSignedIn ? [{ path: '/dashboard', element: <DashboardPage /> }] : []),
    // Bookshelf is standalone — same pattern as Dashboard, own AppAppBar. Proto — dev-only.
    ...(isSignedIn && import.meta.env.DEV ? [{ path: '/bookshelf', element: <BookshelfPage /> }] : []),
    // Workspace pages — fullscreen, no AppAppBar, WorkspaceLayout wraps all workspace routes. Proto's — dev-only.
    ...(isSignedIn && import.meta.env.DEV ? [{ path: '/', element: <WorkspaceLayout />, children: workspaceChildren }] : []),
    // Admin is standalone — always accessible regardless of siteConfig or auth state
    { path: '/admin', element: <AdminPage /> },
    {
        path: '*',
        element: <Navigate to="/" />,
    },
];
